import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import FileSaver from 'file-saver';

interface Report {
	employee: string;
	projectName: string;
	activityType: string;
	feature: string;
	workDescription: string;
	activityDate: string;
	activityDuration: string;
	workMode: string;
}

const generateExcelReport = async (
	project: any,
	firstDayEpoch: any,
	lastDayEpoch: any,
	employees: any[],
	constants: any,
	generateHeadersForMonth: (firstDayEpoch: number, lastDayEpoch: number, excludeWeekends: boolean) => string[],
	getMonthlySummaryAllExceptions: (requests: any[]) => Promise<any[]>
) => {
	const totalEmployeesInProject = project.employeesWorking;
	const currentYear = dayjs().year();
	// const monthNumber = dayjs(`${monthSelected} 01, ${currentYear}`, 'MMMM DD, YYYY').month() + 1;
	// const firstDayEpoch = dayjs(`${monthSelected} 01, ${currentYear}`, 'MMMM DD, YYYY').startOf('month').unix() * 1000;
	// const lastDayEpoch = dayjs(`${monthSelected} 01, ${currentYear}`, 'MMMM DD, YYYY').endOf('month').unix() * 1000;
	const requests: any[] = totalEmployeesInProject.map((employee: string) => {
		const teamUser = employees.find((user: any) => `${user.firstName} ${user.lastName}` === employee);
		return teamUser ? {
			username: teamUser.userName,
			startDate: firstDayEpoch,
			endDate: lastDayEpoch
		} : null;
	}).filter(Boolean);
	const responses = await getMonthlySummaryAllExceptions(requests);
	const reportArray: Report[] = [];
	responses.forEach((response, index) => {
		const userReports = response.data.res.Items;
		const employee = totalEmployeesInProject[index];
		userReports.forEach((userReport: any) => {
			reportArray.push({ ...userReport, employee });
		});
	});
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(`${firstDayEpoch}-${lastDayEpoch}`);
	const headers = generateHeadersForMonth(firstDayEpoch, lastDayEpoch, true);
	worksheet.columns = [
		{ header: 'Employee Name', key: 'Employee Name', width: 20 },
		{ header: 'ProjectName', key: 'ProjectName', width: 20 },
		{ header: 'Category', key: 'Category', width: 20 },
		{ header: 'Feature', key: 'Feature', width: 20 },
		...headers.slice(4).map(date => ({ header: date, key: date, width: 12 }))
	];
	headers.forEach((header, index) => {
		const cell = worksheet.getCell(1, index + 1);
		cell.value = header;
		cell.fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFF26A23' } // Orange color
		};
		cell.font = { color: { argb: 'FFFFFFFF' } };
	});
	const data: any[] = [];
	for (const report of reportArray) {
		const reportDate = dayjs(report.activityDate).format(constants.DATE_FORMAT);
		if (dayjs(reportDate, constants.DATE_FORMAT).day() === 0 || dayjs(reportDate, constants.DATE_FORMAT).day() === 6) {
			continue;
		}
		const existingRow = data.find(row =>
			row['Employee Name'] === report.employee &&
			row['ProjectName'] === report.projectName &&
			row['Category'] === report.activityType &&
			row['Feature'] === report.feature
		);
		if (existingRow) {
			existingRow[reportDate] = (parseFloat(existingRow[reportDate]) || 0) + (parseFloat(report.activityDuration) || 0);
		} else {
			const myReport: any = {};
			myReport['Employee Name'] = report.employee;
			myReport['ProjectName'] = report.projectName;
			myReport['Category'] = report.activityType;
			myReport['Feature'] = report.feature;
			headers.slice(4).forEach(date => {
				myReport[date] = '';
			});

			myReport[reportDate] =  isNaN(parseFloat(report.activityDuration))
			? 0
			: parseFloat(parseFloat(report.activityDuration).toFixed(2));

			data.push(myReport);
		}
	}
	const modifiedData = data.filter((item: any) => item.ProjectName === project.displayProjectName);
	if (modifiedData.length === 0) {
		const emptyReport: any = {
			'Employee Name': '',
			'ProjectName': '    ',
			'Category': '',
			'Feature': '',
		};
		headers.slice(4).forEach(date => {
			emptyReport[date] = '';
		});
		modifiedData.push(emptyReport);
	}
	modifiedData.forEach((row) => {
		const dataRow = worksheet.addRow(headers.map(header => row[header]));
		dataRow.eachCell((cell, colNumber) => {
			const header = headers[colNumber - 1];
			const day = dayjs(header, constants.DATE_FORMAT).day();
			if (day === 0 || day === 6) {
				cell.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: { argb: 'FFC0C0C0' }
				};
				cell.protection = { locked: true };
			}
		});
	});
	worksheet.views = [
		{ state: 'frozen', xSplit: 4, ySplit: 1 }
	];
	const buffer = await workbook.xlsx.writeBuffer();
	const fileName = 'TimelineReport' + dayjs(firstDayEpoch).format(constants.DATE_FORMAT) + '_to_' + dayjs(lastDayEpoch).format(constants.DATE_FORMAT) + '.xlsx';
	const file = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
	FileSaver.saveAs(file, fileName);
};

export default generateExcelReport;
